import React from 'react';
import { Box, Typography, Button, Card, CardContent } from '@mui/material';
import StyledAccordion from '../../../_shared/accordion';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import srcIcon from 'assets/images/User-Question.png';
import { useSurveyContent } from '../../../../shared/hooks';
import titleCase from '../../../../shared/helpers/titleCase';

const DisqualifiedPage: React.FC = () => {
  const { surveyContent } = useSurveyContent();
  const cdUserName = surveyContent?.cd_associate_name ?? '';
  const contactEmail = surveyContent?.cd_associate_email;
  const source = 'Consultations';
  const environment = process.env.REACT_APP_ENV;
  let suffix = '';
  const survey_id = surveyContent?.survey_id?.toString();
  const reasons = surveyContent?.content?.disqualification_reasons ?? [];
  const SEID = surveyContent?.subject ?? '';
  if (environment === 'local' || environment === 'development') {
    suffix = '.dev';
  } else if (environment === 'staging') {
    suffix = '.staging';
  }

  const displayName = (() => {
    const parts = cdUserName.trim().split(/\s+/);
    if (parts.length === 0) return '';
    if (parts.length === 1) return parts[0]; // only one word
    return `${parts[0]} ${parts[1][0].toUpperCase()}.`;
  })();

  // UTM Sign-up URL
  const signUpUrl = `https://professionals${suffix}.newtonx.com/?utm_source=${source}&utm_medium=survey-experience&utm_campaign=consultations-${SEID}&utm_content=screened-out-page&utm_term=survey_id_${survey_id}`;
  const accordionItems = [
    {
      title: 'Am I going to get paid?',
      content: (
        <Typography variant="body2">
          Because you did not advance beyond the screener, you are not eligible for payment.
        </Typography>
      ),
    },
    {
      title: 'Why didn’t I qualify for this survey?',
      content: (
        <>
          <Typography variant="body2">
            Eligibility criteria are based on survey-specific requirements set by the client. These
            may include:
          </Typography>
          <ul style={{ paddingLeft: '1.2rem', margin: 0 }}>
            {reasons.map((r, i) => (
              <li key={i}>
                <Typography variant="body2">{r}</Typography>
              </li>
            ))}
          </ul>
        </>
      ),
    },
    {
      title: 'I don’t see my question answered here.',
      content: (
        <Typography variant="body2">
          Please contact professionals@newtonx.com and we’ll be happy to assist you.
        </Typography>
      ),
    },
  ];
  const mailtoLink = `mailto:${contactEmail}`;
  return (
    <Box
      sx={{
        maxWidth: '480px',
        background: 'white',
        borderRadius: '16px',
        boxShadow:
          ' 0px 5px 12px 4px rgba(0, 0, 0, 0.09), 0px 3px 6px 0px rgba(0, 0, 0, 0.12),  0px 1px 2px -2px rgba(0, 0, 0, 0.16)',
        mx: 'auto',
        px: 2,
        py: 2,
        display: 'flex',
        flexDirection: 'column',
        gap: 1,
      }}
    >
      <Typography variant="h3" fontWeight={700}>
        Thank You for Your Interest
      </Typography>

      <Typography variant="body2">
        We appreciate your time, but unfortunately your profile doesn’t match the specific
        requirements for this consultation opportunity.
      </Typography>

      <Card variant="outlined">
        <CardContent
          sx={{ display: 'flex', flexDirection: 'column', alignItems: 'left', textAlign: 'left' }}
        >
          <Typography variant="subtitle2" fontWeight={700} mb={1}>
            Have Questions?
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'row', gap: 1, mb: '16px' }}>
            <AccountCircleIcon sx={{ height: '40px', width: '40px' }} />
            <Box>
              <Typography variant="subtitle1" fontWeight={400} fontSize="16px">
                {displayName && displayName.length > 1 ? titleCase(displayName) : ''}
              </Typography>
              <Typography variant="caption">{contactEmail}</Typography>
            </Box>
          </Box>
          <Button
            variant="outlined"
            component="a"
            href={mailtoLink}
            target="_blank"
            rel="noopener noreferrer"
          >
            CONTACT
          </Button>
        </CardContent>
      </Card>

      <Card variant="outlined">
        <CardContent sx={{ textAlign: 'center' }}>
          <Typography variant="subtitle2" fontWeight={700} mb="8px">
            Unlock more opportunities!
          </Typography>
          <Typography variant="body2" mb="16px">
            Your latest experience helps us find surveys tailored to you. Take a moment to update
            your NewtonX Professionals Community profile
          </Typography>
          <Button
            variant="text"
            color="primary"
            fullWidth
            onClick={() => {
              window.open(signUpUrl, '_blank', 'noopener,noreferrer');
            }}
          >
            UPDATE PROFILE
          </Button>
        </CardContent>
      </Card>

      <Box>
        <Box display="flex" alignItems="center" mt={1} mb={1}>
          <Box component="img" src={srcIcon} alt="faq icon" sx={{ width: 24, height: 24, mr: 1 }} />
          <Typography variant="subtitle1" fontWeight={700}>
            Frequently Asked Questions
          </Typography>
        </Box>
        <StyledAccordion items={accordionItems} />
      </Box>
    </Box>
  );
};

export default DisqualifiedPage;
